import {
  Assignment as AssignmentIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  StarBorder as StarBorderIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { OrderStatusStatistics } from "../../../types/admin";

interface OrderStatusStatisticsCardsProps {
  statistics: OrderStatusStatistics | null;
  loading?: boolean;
}

const OrderStatusStatisticsCards: React.FC<OrderStatusStatisticsCardsProps> = ({
  statistics,
  loading = false,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Grid container spacing={3} mb={3}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card>
              <CardContent>
                <LinearProgress />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  const cards = [
    {
      title: t("admin.orderStatus.totalStatuses"),
      value: statistics?.totalStatuses ?? 0,
      icon: <AssignmentIcon sx={{ fontSize: 32 }} />,
      color: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      textColor: "white",
      description: t("admin.orderStatus.allOrderStatuses"),
    },
    {
      title: t("admin.orderStatus.activeStatuses"),
      value: statistics?.activeStatuses ?? 0,
      icon: <CheckCircleIcon sx={{ fontSize: 32 }} />,
      color: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
      textColor: "white",
      description: t("admin.orderStatus.currentlyActive"),
    },
    {
      title: t("admin.orderStatus.inactiveStatuses"),
      value: statistics?.inactiveStatuses ?? 0,
      icon: <CancelIcon sx={{ fontSize: 32 }} />,
      color: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
      textColor: "white",
      description: t("admin.orderStatus.currentlyInactive"),
    },
    {
      title: t("admin.orderStatus.statusesInUse"),
      value: statistics?.statusesInUse ?? 0,
      icon: <TrendingUpIcon sx={{ fontSize: 32 }} />,
      color: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
      textColor: "#374151",
      description: t("admin.orderStatus.usedByOrders"),
    },
  ];

  return (
    <Grid container spacing={3} mb={3}>
      {cards.map((card, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Card
            sx={{
              position: "relative",
              overflow: "visible",
              background: card.color,
              color: card.textColor,
              borderRadius: 2,
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            }}
          >
            <CardContent sx={{ pb: 2 }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color:
                        card.textColor === "white"
                          ? "rgba(255, 255, 255, 0.8)"
                          : "rgba(55, 65, 81, 0.7)",
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    {card.title}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      color: card.textColor,
                    }}
                  >
                    {card.value.toLocaleString()}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        card.textColor === "white"
                          ? "rgba(255, 255, 255, 0.9)"
                          : "rgba(55, 65, 81, 0.8)",
                    }}
                  >
                    {card.description}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 2,
                    backgroundColor:
                      card.textColor === "white"
                        ? "rgba(255, 255, 255, 0.2)"
                        : "rgba(55, 65, 81, 0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {card.icon}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}

      {/* Most/Least Used Status */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t("admin.orderStatus.usageInsights")}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <StarIcon color="primary" />
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.orderStatus.mostUsed")}:
                  </Typography>
                  <Chip
                    label={statistics?.mostUsedStatus ?? t("common.none")}
                    color="primary"
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <StarBorderIcon color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.orderStatus.leastUsed")}:
                  </Typography>
                  <Chip
                    label={statistics?.leastUsedStatus ?? t("common.none")}
                    color="default"
                    size="small"
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default OrderStatusStatisticsCards;
